import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import ClosingCountdown from "../components/ClosingCountdown";
import TrustBadges from "../components/TrustBadges";

// /welcome — high-conversion landing page mirroring the 30s social ad.
// Designed for paid-traffic clicks (Meta / YouTube).  Keeps copy verbatim with
// the ad voiceover so the visitor experiences scent-match the moment they land.
export default function Welcome() {
  const { user } = useAuth();
  const { t } = useI18n();

  const isPaid = !!user?.has_paid;
  const ctaTo = !user ? "/register" : isPaid ? "/dashboard" : "/paywall";
  const ctaLabel = !user
    ? t("welcome_cta_primary")
    : isPaid
    ? t("welcome_cta_already_in")
    : t("welcome_cta_pay");

  return (
    <div className="min-h-screen bg-[#F4F4F4] text-black" data-testid="welcome-page">
      {/* HERO — matches ad seconds 0:00–0:09 */}
      <section className="border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 pt-12 sm:pt-20 pb-16 sm:pb-24">
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000] flex items-center gap-2" style={{ fontWeight: 900 }}>
            <span className="w-2 h-2 bg-[#E00000]" />
            {t("welcome_eyebrow")}
          </div>
          <h1
            className="mt-4 font-serif-display text-black leading-[0.9] tracking-tight text-5xl sm:text-7xl md:text-8xl"
            style={{ fontWeight: 800 }}
            data-testid="welcome-h1"
          >
            {t("welcome_h1_a")}
            <br />
            <span className="text-[#E00000]">{t("welcome_h1_b")}</span>
          </h1>
          <p className="mt-6 text-black max-w-2xl text-base sm:text-lg" style={{ fontWeight: 700 }}>
            {t("welcome_sub")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to={ctaTo}
              className="inline-flex items-center justify-center bg-[#E00000] text-white uppercase tracking-widest px-8 py-5 border-2 border-[#E00000] brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all text-sm"
              style={{ fontWeight: 900 }}
              data-testid="welcome-primary-cta"
            >
              {ctaLabel} →
            </Link>
            <div className="font-mono-civic text-xs uppercase tracking-widest text-black/70" style={{ fontWeight: 800 }}>
              {t("welcome_micro_proof")}
            </div>
          </div>
        </div>
      </section>

      {/* TENSION — ad seconds 0:04–0:09, three-up panel */}
      <section className="border-b-2 border-black bg-black text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000]" style={{ fontWeight: 900 }}>
            {t("welcome_tension_tag")}
          </div>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl md:text-5xl leading-tight" style={{ fontWeight: 800 }} data-testid="welcome-tension-h">
            {t("welcome_tension_h")}
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              ["welcome_pain_1_h", "welcome_pain_1_p"],
              ["welcome_pain_2_h", "welcome_pain_2_p"],
              ["welcome_pain_3_h", "welcome_pain_3_p"],
            ].map(([h, p], i) => (
              <div key={h} className="border-2 border-white p-5" data-testid={`welcome-pain-${i + 1}`}>
                <div className="font-mono-civic text-[10px] uppercase tracking-widest text-[#E00000]" style={{ fontWeight: 900 }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-2 font-serif-display text-2xl leading-tight" style={{ fontWeight: 800 }}>
                  {t(h)}
                </div>
                <div className="mt-2 text-sm" style={{ fontWeight: 600 }}>
                  {t(p)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM INTRO — ad seconds 0:09–0:15 */}
      <section className="border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-7">
              <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000]" style={{ fontWeight: 900 }}>
                {t("welcome_intro_tag")}
              </div>
              <h2 className="mt-2 font-serif-display text-3xl sm:text-5xl leading-tight" style={{ fontWeight: 800 }}>
                {t("welcome_intro_h")}
              </h2>
              <p className="mt-4 text-base sm:text-lg" style={{ fontWeight: 700 }}>
                {t("welcome_intro_p")}
              </p>
              <ul className="mt-6 space-y-2 font-mono-civic text-sm" style={{ fontWeight: 800 }}>
                {[
                  "welcome_bullet_1",
                  "welcome_bullet_2",
                  "welcome_bullet_3",
                  "welcome_bullet_4",
                ].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <span className="text-[#E00000] mt-0.5">▸</span>
                    <span>{t(k)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5 bg-white border-2 border-black brutal-shadow p-6">
              <div className="font-mono-civic text-xs uppercase tracking-widest text-[#E00000]" style={{ fontWeight: 900 }}>
                {t("welcome_price_tag")}
              </div>
              <div className="mt-2 font-serif-display text-5xl sm:text-6xl text-black leading-none" style={{ fontWeight: 800 }} data-testid="welcome-price">
                $2.49 <span className="text-2xl align-top">CAD</span>
              </div>
              <div className="mt-1 font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
                {t("welcome_price_sub")}
              </div>
              <div className="mt-4 text-sm" style={{ fontWeight: 700 }}>
                {t("welcome_price_p")}
              </div>
              <Link
                to={ctaTo}
                className="mt-6 block text-center bg-black text-white uppercase tracking-widest py-4 border-2 border-black hover:bg-white hover:text-black transition-colors text-sm"
                style={{ fontWeight: 900 }}
                data-testid="welcome-price-cta"
              >
                {ctaLabel} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COUNTDOWN — ad seconds 0:20–0:25 (election urgency) */}
      <section className="border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
          <ClosingCountdown />
        </div>
      </section>

      {/* TRUST — head off the "is it safe to enter my card?" objection */}
      <section className="border-b-2 border-black bg-[#F4F4F4]">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
          <TrustBadges />
        </div>
      </section>

      {/* FINAL CTA — ad seconds 0:25–0:30 */}
      <section className="bg-[#E00000] text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-16 sm:py-24 text-center">
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em]" style={{ fontWeight: 900 }}>
            {t("welcome_final_tag")}
          </div>
          <h2 className="mt-3 font-serif-display text-5xl sm:text-7xl leading-[0.9]" style={{ fontWeight: 800 }} data-testid="welcome-final-h">
            {t("welcome_final_h")}
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg" style={{ fontWeight: 700 }}>
            {t("welcome_final_p")}
          </p>
          <div className="mt-8">
            <Link
              to={ctaTo}
              className="inline-flex items-center justify-center bg-white text-[#E00000] uppercase tracking-widest px-10 py-5 border-2 border-white text-sm"
              style={{ fontWeight: 900 }}
              data-testid="welcome-final-cta"
            >
              {ctaLabel} →
            </Link>
          </div>
          <div className="mt-6 font-mono-civic text-[10px] uppercase tracking-widest text-white/70" style={{ fontWeight: 800 }}>
            {t("welcome_final_micro")}
          </div>
        </div>
      </section>
    </div>
  );
}
